'use client'

import type { ElevationPoint } from '@/lib/types'
import { cn } from '@/lib/utils'

interface ElevationChartProps {
	profile: ElevationPoint[]
	className?: string
}

/**
 * SVG-based elevation profile chart.
 * Shows elevation (ft) vs distance (mi) along the route.
 */
export function ElevationChart({ profile, className }: ElevationChartProps) {
	if (profile.length < 2) return null

	const width = 600
	const height = 150
	const padding = { top: 10, right: 10, bottom: 30, left: 50 }
	const chartW = width - padding.left - padding.right
	const chartH = height - padding.top - padding.bottom

	const maxDist = Math.max(...profile.map((p) => p.distance))
	const elevations = profile.map((p) => p.elevation)
	const minElev = Math.min(...elevations)
	const maxElev = Math.max(...elevations)
	const elevRange = maxElev - minElev || 1

	// Scale functions
	const x = (d: number) => padding.left + (d / maxDist) * chartW
	const y = (e: number) => padding.top + chartH - ((e - minElev) / elevRange) * chartH

	// Build path
	const linePath = profile
		.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.distance).toFixed(1)},${y(p.elevation).toFixed(1)}`)
		.join(' ')

	// Fill path (closed to bottom)
	const fillPath = `${linePath} L${x(maxDist).toFixed(1)},${(padding.top + chartH).toFixed(1)} L${padding.left},${(padding.top + chartH).toFixed(1)} Z`

	// Y-axis ticks (3-5 labels)
	const yTicks: number[] = []
	const tickCount = 4
	for (let i = 0; i <= tickCount; i++) {
		yTicks.push(Math.round(minElev + (elevRange * i) / tickCount))
	}

	// X-axis ticks
	const xTicks: number[] = []
	const xTickCount = 4
	for (let i = 0; i <= xTickCount; i++) {
		xTicks.push(Math.round((maxDist * i) / xTickCount))
	}

	return (
		<div className={cn('w-full', className)}>
			<div className="mb-1.5 flex items-center justify-between">
				<span className="text-xs font-medium text-text-secondary">Elevation Profile</span>
				<span className="font-mono text-[10px] text-text-muted">
					{Math.round(minElev).toLocaleString()}–{Math.round(maxElev).toLocaleString()} ft
				</span>
			</div>
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full"
				preserveAspectRatio="xMidYMid meet"
				role="img"
				aria-label={`Elevation profile: ${Math.round(minElev).toLocaleString()} to ${Math.round(maxElev).toLocaleString()} feet`}
			>
				<title>Elevation profile along route</title>
				{/* Gradient fill */}
				<defs>
					<linearGradient id="elevFill" x1="0" x2="0" y1="0" y2="1">
						<stop offset="0%" stopColor="#9370DB" stopOpacity="0.3" />
						<stop offset="100%" stopColor="#9370DB" stopOpacity="0.02" />
					</linearGradient>
				</defs>

				{/* Grid lines */}
				{yTicks.map((tick) => (
					<line
						key={tick}
						x1={padding.left}
						x2={width - padding.right}
						y1={y(tick)}
						y2={y(tick)}
						stroke="var(--clb-border)"
						strokeWidth="0.5"
						strokeDasharray="2,2"
					/>
				))}

				{/* Fill area */}
				<path d={fillPath} fill="url(#elevFill)" />

				{/* Line */}
				<path
					d={linePath}
					fill="none"
					stroke="#9370DB"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>

				{/* Y-axis labels */}
				{yTicks.map((tick) => (
					<text
						key={tick}
						x={padding.left - 6}
						y={y(tick) + 3}
						textAnchor="end"
						className="fill-text-muted"
						fontSize="9"
						fontFamily="var(--font-mono)"
					>
						{tick.toLocaleString()}
					</text>
				))}

				{/* X-axis labels */}
				{xTicks.map((tick) => (
					<text
						key={tick}
						x={x(tick)}
						y={height - 6}
						textAnchor="middle"
						className="fill-text-muted"
						fontSize="9"
						fontFamily="var(--font-mono)"
					>
						{tick} mi
					</text>
				))}
			</svg>
		</div>
	)
}
